import React, { useCallback, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import DOMPurify from 'dompurify';
import { Box, Button } from "@mui/material";
import Style from './ContentEditor.module.scss';
import contentStyle from '../content/RichContent.module.scss';

function ToolButton({ onClick, active, children, title }) {
    return (
        <Button
            size={'small'}
            variant={'text'}
            onMouseDown={(e) => e.preventDefault()}
            onClick={onClick}
            title={title}
            className={`${Style.toolButton} ${active ? Style.toolButtonActive : ''}`}
        >
            {children}
        </Button>
    );
}

export default function ContentEditor() {
    const fileInputRef = useRef(null);
    const imageInputRef = useRef(null);
    const [copied, setCopied] = useState(false);
    const [previewHtml, setPreviewHtml] = useState('');

    const editor = useEditor({
        extensions: [
            StarterKit,
            Underline,
            Link.configure({ openOnClick: false, autolink: true }),
            Image,
            Placeholder.configure({ placeholder: 'Write or paste your blog / case study content here...' }),
            Table.configure({ resizable: true }),
            TableRow,
            TableHeader,
            TableCell,
        ],
        content: '<h1>Untitled</h1><p></p>',
    });

    const refreshPreview = useCallback(() => {
        if (!editor) return;
        setPreviewHtml(DOMPurify.sanitize(editor.getHTML()));
    }, [editor]);

    if (!editor) return null;

    const setLink = () => {
        const previousUrl = editor.getAttributes('link').href;
        const url = window.prompt('Link URL', previousUrl || 'https://');
        if (url === null) return;
        if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
        }
        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    };

    const addImage = () => {
        imageInputRef.current?.click();
    };

    const insertImageFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            editor.chain().focus().setImage({ src: String(reader.result || '') }).run();
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const copyHtml = async () => {
        const html = editor.getHTML();
        await navigator.clipboard.writeText(html);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    const downloadHtml = () => {
        const html = editor.getHTML();
        const blob = new Blob([html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'content.html';
        a.click();
        URL.revokeObjectURL(url);
    };

    const loadHtml = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            editor.commands.setContent(String(reader.result || ''));
        };
        reader.readAsText(file);
        e.target.value = '';
    };

    return (
        <Box className={Style.page}>
            <Box component={'h1'} className={Style.heading}>Content Editor</Box>
            <Box className={Style.subheading}>
                Write or paste your blog/case study content, then download the HTML and drop it into
                <code> src/components/blogs/content/</code> or <code>src/components/casestudies/content/</code>.
                Render it on a page with the <code>RichContent</code> component, the same way existing blogs load their <code>.txt</code> files.
            </Box>

            <Box className={Style.toolbar}>
                <ToolButton title={'Undo'} onClick={() => editor.chain().focus().undo().run()}>↺</ToolButton>
                <ToolButton title={'Redo'} onClick={() => editor.chain().focus().redo().run()}>↻</ToolButton>
                <Box className={Style.divider} />
                <ToolButton title={'Bold'} active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}>B</ToolButton>
                <ToolButton title={'Italic'} active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}>I</ToolButton>
                <ToolButton title={'Underline'} active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}>U</ToolButton>
                <ToolButton title={'Strikethrough'} active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()}>S</ToolButton>
                <ToolButton title={'Inline code'} active={editor.isActive('code')} onClick={() => editor.chain().focus().toggleCode().run()}>{'</>'}</ToolButton>
                <Box className={Style.divider} />
                <ToolButton title={'Heading 1'} active={editor.isActive('heading', { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>H1</ToolButton>
                <ToolButton title={'Heading 2'} active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>H2</ToolButton>
                <ToolButton title={'Heading 3'} active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>H3</ToolButton>
                <ToolButton title={'Paragraph'} active={editor.isActive('paragraph')} onClick={() => editor.chain().focus().setParagraph().run()}>P</ToolButton>
                <Box className={Style.divider} />
                <ToolButton title={'Bullet list'} active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}>• List</ToolButton>
                <ToolButton title={'Numbered list'} active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}>1. List</ToolButton>
                <ToolButton title={'Blockquote'} active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()}>❝</ToolButton>
                <ToolButton title={'Code block'} active={editor.isActive('codeBlock')} onClick={() => editor.chain().focus().toggleCodeBlock().run()}>{'{ }'}</ToolButton>
                <ToolButton title={'Horizontal rule'} onClick={() => editor.chain().focus().setHorizontalRule().run()}>―</ToolButton>
                <Box className={Style.divider} />
                <ToolButton title={'Link'} active={editor.isActive('link')} onClick={setLink}>Link</ToolButton>
                <ToolButton title={'Image'} onClick={addImage}>Img</ToolButton>
                <input
                    ref={imageInputRef}
                    type={'file'}
                    accept={'image/*'}
                    className={Style.hiddenInput}
                    onChange={insertImageFile}
                />
                <Box className={Style.divider} />
                <ToolButton title={'Insert table'} onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}>Table</ToolButton>
                <ToolButton title={'Add column'} onClick={() => editor.chain().focus().addColumnAfter().run()}>+Col</ToolButton>
                <ToolButton title={'Add row'} onClick={() => editor.chain().focus().addRowAfter().run()}>+Row</ToolButton>
                <ToolButton title={'Delete table'} onClick={() => editor.chain().focus().deleteTable().run()}>Del Table</ToolButton>
            </Box>

            <Box className={Style.editorFrame}>
                <EditorContent editor={editor} />
            </Box>

            <Box className={Style.actions}>
                <Button variant={'contained'} onClick={copyHtml}>{copied ? 'Copied!' : 'Copy HTML'}</Button>
                <Button variant={'outlined'} onClick={downloadHtml}>Download .html</Button>
                <Button variant={'outlined'} onClick={() => fileInputRef.current?.click()}>Load existing file</Button>
                <Button variant={'text'} onClick={refreshPreview}>Refresh Preview</Button>
                <input
                    ref={fileInputRef}
                    type={'file'}
                    accept={'.html,.htm,.txt,text/html,text/plain'}
                    className={Style.hiddenInput}
                    onChange={loadHtml}
                />
            </Box>

            {previewHtml && (
                <Box className={Style.preview}>
                    <Box className={Style.previewLabel}>Live preview (styled as it will appear on the site)</Box>
                    <Box
                        className={contentStyle.richContent}
                        dangerouslySetInnerHTML={{ __html: previewHtml }}
                    />
                </Box>
            )}
        </Box>
    );
}
